import posthog from 'posthog-js';

import {
  frameTypeOf,
  redactSensitive,
} from 'src/store/messages/chatDiagnosticText';
import {
  getChatSocketLog,
  type SocketLogEntry,
} from 'src/store/messages/chatSocketLog';

// Tells apart "the network blocks the chat host", "the upgrade completes but no traffic flows"
// and "not the network" when the chat connect fails. The short probe is an unauthenticated
// handshake; the realistic one has the length and shape of the real connect URL, which a proxy
// with URL-length or token-pattern rules could treat differently.
export const CHAT_CONNECT_DIAGNOSTIC_EVENT = 'chat_connect_diagnostic';

const PROBE_TIMEOUT_MS = 8000;
const MAX_REPORTS_PER_PAGE_LOAD = 3;
// Mirrors the baseURL App.tsx gives the chat client; used only if the client reports none.
const DEFAULT_CHAT_BASE_URL = 'https://chat.stream-io-api.com';

// Fake credentials sized like the real ones; never a real token.
const FAKE_TOKEN = [
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9',
  'x'.repeat(150),
  'x'.repeat(43),
].join('.');
const FAKE_CONNECT_JSON = JSON.stringify({
  user_id: 'probe',
  user_details: { id: 'probe', name: 'probe', image: 'x'.repeat(120) },
  user_agent: 'x'.repeat(100),
  server_determines_connection_id: true,
});

export interface HttpsProbeResult {
  ok: boolean;
  ms: number;
}

export interface SocketProbeResult {
  /** True once the first frame arrived, which is what a working connection needs. */
  ok: boolean;
  ms: number;
  upgraded: boolean;
  firstFrameMs?: number;
  frameType?: string;
  closeCode?: number;
}

type ProbeShape = 'short' | 'realistic';

interface Probes {
  https: HttpsProbeResult;
  websocket: SocketProbeResult;
  websocketRealistic: SocketProbeResult;
}

let reportCount = 0;
let probes: Promise<Probes> | undefined;

const elapsed = (startedAt: number) =>
  Math.round(performance.now() - startedAt);

export async function probeChatHttps(
  baseUrl: string,
): Promise<HttpsProbeResult> {
  const startedAt = performance.now();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    // no-cors resolves with an opaque response when the host is reachable and rejects
    // only on a network-level failure, which is the signal needed.
    await fetch(baseUrl, { mode: 'no-cors', signal: controller.signal });
    return { ok: true, ms: elapsed(startedAt) };
  } catch {
    return { ok: false, ms: elapsed(startedAt) };
  } finally {
    clearTimeout(timer);
  }
}

const probeUrl = (baseUrl: string, apiKey: string, shape: ProbeShape) => {
  const url = new URL('/connect', baseUrl);
  url.protocol = 'wss:';
  url.searchParams.set('api_key', apiKey);
  if (shape === 'realistic') {
    url.searchParams.set('json', FAKE_CONNECT_JSON);
    url.searchParams.set('authorization', FAKE_TOKEN);
    url.searchParams.set('stream-auth-type', 'jwt');
    url.searchParams.set('X-Stream-Client', 'probe');
  }
  return url.toString();
};

export function probeChatWebsocket(
  baseUrl: string,
  apiKey: string,
  shape: ProbeShape = 'short',
): Promise<SocketProbeResult> {
  const startedAt = performance.now();
  return new Promise((resolve) => {
    let upgraded = false;
    let firstFrameMs: number | undefined;
    let frameType: string | undefined;
    let closeCode: number | undefined;
    let settled = false;
    let socket: WebSocket | undefined;

    const settle = () => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      try {
        socket?.close();
      } catch {
        // already closed
      }
      resolve({
        ok: firstFrameMs !== undefined,
        ms: elapsed(startedAt),
        upgraded,
        firstFrameMs,
        frameType,
        closeCode,
      });
    };

    const timer = setTimeout(settle, PROBE_TIMEOUT_MS);

    try {
      // Unauthenticated on purpose: the server completes the upgrade and answers with an error
      // frame, so a first frame proves traffic flows both ways.
      socket = new WebSocket(probeUrl(baseUrl, apiKey, shape));
    } catch {
      settle();
      return;
    }
    socket.onopen = () => {
      upgraded = true;
    };
    socket.onmessage = (event: MessageEvent<unknown>) => {
      firstFrameMs = elapsed(startedAt);
      frameType = frameTypeOf(event.data);
      settle();
    };
    socket.onerror = settle;
    socket.onclose = (event) => {
      closeCode = event.code;
      settle();
    };
  });
}

const runProbes = async (baseUrl: string, apiKey: string): Promise<Probes> => {
  const [https, websocket, websocketRealistic] = await Promise.all([
    probeChatHttps(baseUrl),
    probeChatWebsocket(baseUrl, apiKey, 'short'),
    probeChatWebsocket(baseUrl, apiKey, 'realistic'),
  ]);
  return { https, websocket, websocketRealistic };
};

const connectionInfo = () => {
  const { connection } = navigator as Navigator & {
    connection?: { effectiveType?: string };
  };
  return {
    online: navigator.onLine,
    effectiveType: connection?.effectiveType,
    visibility: document.visibilityState,
  };
};

const ERROR_MESSAGE_MAX_LENGTH = 200;

const rawErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error) ?? '';
  } catch {
    return '';
  }
};

// The message is the only free text from the error recorded, so strip anything token-shaped.
export const describeError = (error: unknown) => ({
  errorName: error instanceof Error ? error.name : typeof error,
  errorMessage: redactSensitive(rawErrorMessage(error)).slice(
    0,
    ERROR_MESSAGE_MAX_LENGTH,
  ),
});

// Why the server refused the connection, pulled out of the socket log so it is queryable directly.
const lastServerError = (log: SocketLogEntry[]) => {
  const entry = [...log]
    .reverse()
    .find((line) => line.errorMessage !== undefined);
  return (
    entry && {
      code: entry.errorCode,
      status: entry.errorStatus,
      message: entry.errorMessage,
    }
  );
};

// `token` is the token request to our API, `connect` is the chat connection itself.
export type ChatConnectStage = 'token' | 'connect';

export interface ChatConnectFailure {
  stage: ChatConnectStage;
  baseUrl: string | undefined;
  apiKey: string;
  error: unknown;
  /** performance.now() when initialization started. */
  startedAt: number;
  /** How long the token request took, once it has succeeded. */
  tokenMs?: number;
}

/** Records up to three failures per page load, probing the chat host once. Never throws. */
export async function reportChatConnectFailure({
  stage,
  baseUrl: configuredBaseUrl,
  apiKey,
  error,
  startedAt,
  tokenMs,
}: ChatConnectFailure): Promise<void> {
  if (reportCount >= MAX_REPORTS_PER_PAGE_LOAD) {
    return;
  }
  reportCount += 1;
  const attempt = reportCount;
  // Taken before the probes run so they describe the failed attempt, not the diagnostic.
  const msSinceStart = elapsed(startedAt);
  const socketLog = getChatSocketLog().map((entry) => ({
    ...entry,
    at: Math.round(entry.at - startedAt),
  }));
  // The log is shared across attempts, so only this attempt's lines can explain this failure.
  const serverError = lastServerError(
    socketLog.filter((entry) => entry.at >= 0),
  );
  const baseUrl = configuredBaseUrl ?? DEFAULT_CHAT_BASE_URL;

  try {
    probes ??= runProbes(baseUrl, apiKey);

    const payload = {
      attempt,
      stage,
      msSinceStart,
      tokenMs,
      isWSFailure: Boolean((error as { isWSFailure?: boolean })?.isWSFailure),
      ...describeError(error),
      ...(await probes),
      serverError,
      socketLog,
      ...connectionInfo(),
    };
    console.info(`[diag] ${CHAT_CONNECT_DIAGNOSTIC_EVENT}`, payload);
    posthog.capture(CHAT_CONNECT_DIAGNOSTIC_EVENT, payload);
  } catch {
    // diagnostics must never affect the chat flow
  }
}

export const resetChatConnectDiagnosticsForTests = () => {
  reportCount = 0;
  probes = undefined;
};
